// Bubble Pop - DOM rendering, aim & shoot interaction, and the GameDef export.
//
// Coordinates: everything inside .bp-field uses pixels, derived from the field
// width (d = width / COLS = one bubble diameter). The engine speaks in cells;
// cellCenter() converts cell -> diameter units -> pixels.

import { el, clear } from "../../lib/dom";
import { sfx, haptic } from "../../lib/feedback";
import type { GameDef, LevelView, LevelViewOptions } from "../types";
import {
  COLS,
  DEADLINE_ROW,
  EMPTY,
  MATCH_MIN,
  MAX_ROWS,
  type BubbleLevel,
  type Grid,
  cellCenter,
  cloneGrid,
  cluster,
  countBubbles,
  floatingCells,
  generateBubbleLevel,
  makeRng,
  neighbors,
  nextColor,
  queueSeed,
  rowWidth,
} from "./engine";
import "./bubblepop.css";

// Endless: each level is generated on demand, deterministically from its number.
const levelCache = new Map<number, BubbleLevel>();
function levelForNumber(n: number): BubbleLevel {
  let lvl = levelCache.get(n);
  if (!lvl) {
    lvl = generateBubbleLevel(n);
    levelCache.set(n, lvl);
  }
  return lvl;
}

function starsFor(shots: number, par: number): number {
  if (shots <= par) return 3;
  if (shots <= Math.ceil(par * 1.5)) return 2;
  return 1;
}

const AIM_DOTS = 14;

function createLevelView(opts: LevelViewOptions): LevelView {
  const level = levelForNumber(opts.level);
  const par = level.par;

  let grid: Grid = cloneGrid(level.grid);
  let queueRng = makeRng(queueSeed(level.n));
  let current = nextColor(queueRng, grid);
  let next = nextColor(queueRng, grid);
  let shots = 0;
  let finished = false;
  let busy = false; // a bubble is in flight
  const timers: Array<ReturnType<typeof setTimeout>> = [];
  let raf = 0;

  const root = opts.mount;
  clear(root);
  root.classList.add("bp");

  // ---- DOM skeleton ----
  const fieldEl = el("div.bp-field");
  const deadlineEl = el("div.bp-deadline");
  const cannonEl = el("div.bp-bubble.bp-cannon");
  const nextEl = el("div.bp-bubble.bp-next");
  const shooterEl = el(
    "div.bp-shooter",
    {},
    el("div.bp-next-wrap", {}, el("span.bp-next-label", {}, "Volgende"), nextEl),
    cannonEl,
    el("div.bp-next-spacer"),
  );
  fieldEl.append(deadlineEl);
  const stage = el("div.bp-stage", {}, fieldEl, shooterEl);
  const moveLabel = el("span.bp-moves");
  const toolbar = el(
    "div.bp-toolbar",
    {},
    moveLabel,
    el("button.bp-btn", { onclick: doRestart, "aria-label": "Opnieuw" }, "↺ Opnieuw"),
  );
  root.append(stage, toolbar);

  const aimDots: HTMLElement[] = [];
  for (let i = 0; i < AIM_DOTS; i++) {
    const dot = el("span.bp-aim-dot");
    aimDots.push(dot);
    fieldEl.append(dot);
  }

  let overlay: HTMLElement | null = null;
  const bubbleEls = new Map<string, HTMLElement>();

  function later(fn: () => void, ms: number): void {
    timers.push(setTimeout(fn, ms));
  }

  // ---- Metrics (recomputed on resize) ----
  let d = 0; // bubble diameter in px
  let fieldW = 0;
  let fieldH = 0; // top of the shooter zone == deadline line
  function layout(): void {
    fieldW = fieldEl.clientWidth;
    if (fieldW === 0) {
      raf = requestAnimationFrame(layout);
      return;
    }
    d = fieldW / COLS;
    fieldH = ((DEADLINE_ROW + 1) * 0.866 + 0.5) * d;
    fieldEl.style.height = `${fieldH}px`;
    deadlineEl.style.top = `${fieldH - 2}px`;
    for (const [key, node] of bubbleEls) {
      const [r, c] = key.split(",").map(Number);
      placeAt(node, ...cellPx(r, c));
    }
    renderShooter();
  }
  const onResize = (): void => layout();
  window.addEventListener("resize", onResize);

  function cellPx(r: number, c: number): [number, number] {
    const { x, y } = cellCenter(r, c);
    return [x * d, y * d];
  }

  function shooterPos(): [number, number] {
    return [fieldW / 2, fieldH + 0.85 * d];
  }

  function placeAt(node: HTMLElement, x: number, y: number): void {
    node.style.width = `${d}px`;
    node.style.height = `${d}px`;
    node.style.transform = `translate(${x - d / 2}px, ${y - d / 2}px)`;
  }

  // ---- Rendering ----
  function bubbleNode(color: number): HTMLElement {
    return el(`div.bp-bubble.bp-c-${(color % 6) + 1}`);
  }

  function addBubbleEl(r: number, c: number, color: number, pop = false): void {
    const node = bubbleNode(color);
    if (pop) node.classList.add("bp-in");
    placeAt(node, ...cellPx(r, c));
    fieldEl.append(node);
    bubbleEls.set(`${r},${c}`, node);
  }

  function buildBoard(): void {
    for (const node of bubbleEls.values()) node.remove();
    bubbleEls.clear();
    for (let r = 0; r < MAX_ROWS; r++) {
      for (let c = 0; c < rowWidth(r); c++) {
        if (grid[r][c] !== EMPTY) addBubbleEl(r, c, grid[r][c]);
      }
    }
  }

  // The cannon and next-preview live in the shooter bar (normal flow layout);
  // only their color classes and the cannon's size need updating.
  function renderShooter(): void {
    cannonEl.className = `bp-bubble bp-cannon bp-c-${(current % 6) + 1}`;
    cannonEl.style.width = `${d}px`;
    cannonEl.style.height = `${d}px`;
    nextEl.className = `bp-bubble bp-next bp-c-${(next % 6) + 1}`;
  }

  function updateStatus(): void {
    moveLabel.textContent = `Schoten: ${shots} · Par ${par}`;
    opts.setStatus?.(`🫧 ${countBubbles(grid)}`);
  }

  // ---- Aiming ----
  let aiming = false;

  /** Direction from a pointer event, clamped to always shoot upward. */
  function aimDir(e: PointerEvent): { dx: number; dy: number } | null {
    const rect = fieldEl.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const [sx, sy] = shooterPos();
    let dx = px - sx;
    let dy = py - sy;
    const len = Math.hypot(dx, dy);
    if (len < 6) return null;
    dx /= len;
    dy /= len;
    if (dy > -0.18) {
      // Too flat / downward: clamp to a near-horizontal upward shot.
      dy = -0.18;
      dx = Math.sign(dx || 1) * Math.sqrt(1 - dy * dy);
    }
    return { dx, dy };
  }

  /** Walk the trajectory (with wall bounces) until it would hit something. */
  function tracePath(dx: number, dy: number, maxLen: number): Array<[number, number]> {
    const pts: Array<[number, number]> = [];
    let [x, y] = shooterPos();
    let vx = dx;
    let vy = dy;
    const step = 0.3 * d;
    const occupied: Array<[number, number]> = [];
    for (let r = 0; r < MAX_ROWS; r++) {
      for (let c = 0; c < rowWidth(r); c++) {
        if (grid[r][c] !== EMPTY) occupied.push(cellPx(r, c) as [number, number]);
      }
    }
    for (let t = 0; t < maxLen; t++) {
      x += vx * step;
      y += vy * step;
      if (x < d / 2) {
        x = d - x;
        vx = -vx;
      } else if (x > fieldW - d / 2) {
        x = 2 * (fieldW - d / 2) - x;
        vx = -vx;
      }
      if (y <= d / 2) break;
      let hit = false;
      for (const [ox, oy] of occupied) {
        if ((x - ox) ** 2 + (y - oy) ** 2 < (0.95 * d) ** 2) {
          hit = true;
          break;
        }
      }
      if (hit) break;
      pts.push([x, y]);
    }
    return pts;
  }

  function showAim(dx: number, dy: number): void {
    const pts = tracePath(dx, dy, 60);
    for (let i = 0; i < AIM_DOTS; i++) {
      const p = pts[Math.floor((i + 1) * (pts.length / (AIM_DOTS + 1)))];
      const dot = aimDots[i];
      if (p && pts.length > 2) {
        dot.style.opacity = "1";
        dot.style.transform = `translate(${p[0] - 3}px, ${p[1] - 3}px)`;
      } else {
        dot.style.opacity = "0";
      }
    }
  }

  function hideAim(): void {
    for (const dot of aimDots) dot.style.opacity = "0";
  }

  function onPointerDown(e: PointerEvent): void {
    if (finished || busy || overlay) return;
    const dir = aimDir(e);
    if (!dir) return;
    aiming = true;
    try {
      stage.setPointerCapture(e.pointerId);
    } catch {
      /* synthetic events / stale pointer ids have no capturable pointer */
    }
    showAim(dir.dx, dir.dy);
  }

  function onPointerMove(e: PointerEvent): void {
    if (!aiming) return;
    const dir = aimDir(e);
    if (dir) showAim(dir.dx, dir.dy);
  }

  function onPointerUp(e: PointerEvent): void {
    if (!aiming) return;
    aiming = false;
    hideAim();
    if (finished || busy || overlay) return;
    const dir = aimDir(e);
    if (dir) shoot(dir.dx, dir.dy);
  }

  stage.addEventListener("pointerdown", onPointerDown);
  stage.addEventListener("pointermove", onPointerMove);
  stage.addEventListener("pointerup", onPointerUp);
  stage.addEventListener("pointercancel", () => {
    aiming = false;
    hideAim();
  });

  // ---- Shooting ----
  function shoot(dx: number, dy: number): void {
    busy = true;
    shots++;
    sfx.tap();
    haptic(5);

    const color = current;
    const flying = bubbleNode(color);
    flying.classList.add("bp-flying");
    fieldEl.append(flying);

    let [x, y] = shooterPos();
    let vx = dx;
    let vy = dy;
    const speed = 20 * d; // px per second
    let last = performance.now();

    const occupied: Array<[number, number]> = [];
    for (let r = 0; r < MAX_ROWS; r++) {
      for (let c = 0; c < rowWidth(r); c++) {
        if (grid[r][c] !== EMPTY) occupied.push([r, c]);
      }
    }

    function frame(now: number): void {
      const dt = Math.min(32, now - last) / 1000;
      last = now;
      // Substep to avoid tunneling through a bubble at high speed.
      let remaining = speed * dt;
      let landed = false;
      while (remaining > 0 && !landed) {
        const step = Math.min(remaining, 0.35 * d);
        remaining -= step;
        x += vx * step;
        y += vy * step;
        if (x < d / 2) {
          x = d - x;
          vx = -vx;
        } else if (x > fieldW - d / 2) {
          x = 2 * (fieldW - d / 2) - x;
          vx = -vx;
        }
        if (y <= d / 2) {
          landed = true;
          break;
        }
        for (const [r, c] of occupied) {
          const [ox, oy] = cellPx(r, c);
          if ((x - ox) ** 2 + (y - oy) ** 2 < (0.92 * d) ** 2) {
            landed = true;
            break;
          }
        }
      }
      placeAt(flying, x, y);
      if (!landed) {
        raf = requestAnimationFrame(frame);
        return;
      }
      flying.remove();
      land(x, y, color);
    }
    raf = requestAnimationFrame(frame);
  }

  /** Snap the landed bubble into the nearest sensible empty cell, then resolve. */
  function land(x: number, y: number, color: number): void {
    let bestKey: [number, number] | null = null;
    let bestDist = Infinity;
    let fallbackKey: [number, number] | null = null;
    let fallbackDist = Infinity;
    for (let r = 0; r < MAX_ROWS; r++) {
      for (let c = 0; c < rowWidth(r); c++) {
        if (grid[r][c] !== EMPTY) continue;
        const [ox, oy] = cellPx(r, c);
        const dist = (x - ox) ** 2 + (y - oy) ** 2;
        // Prefer cells anchored to the ceiling or an existing bubble.
        const anchored = r === 0 || neighborsOccupied(r, c);
        if (anchored && dist < bestDist) {
          bestDist = dist;
          bestKey = [r, c];
        }
        if (dist < fallbackDist) {
          fallbackDist = dist;
          fallbackKey = [r, c];
        }
      }
    }
    const target = bestKey ?? fallbackKey;
    if (!target) {
      // Board is completely full - treat as a loss.
      busy = false;
      showLose();
      return;
    }
    const [r, c] = target;
    grid[r][c] = color;
    addBubbleEl(r, c, color, true);

    if (r >= DEADLINE_ROW) {
      busy = false;
      updateStatus();
      showLose();
      return;
    }

    // Pop a big-enough same-color cluster.
    const group = cluster(grid, r, c);
    if (group.length >= MATCH_MIN) {
      for (const [gr, gc] of group) {
        grid[gr][gc] = EMPTY;
        popEl(gr, gc, "bp-pop");
      }
      // Anything left dangling falls off.
      const fall = floatingCells(grid);
      for (const [fr, fc] of fall) {
        grid[fr][fc] = EMPTY;
        popEl(fr, fc, "bp-fall");
      }
      sfx.clear();
      haptic([12, 24, 12]);
    } else {
      sfx.place();
      haptic(6);
    }

    // Advance the queue (colors are drawn from what's still on the board).
    current = next;
    next = nextColor(queueRng, grid);
    renderShooter();
    updateStatus();
    busy = false;

    if (countBubbles(grid) === 0) win();
  }

  function neighborsOccupied(r: number, c: number): boolean {
    return neighbors(r, c).some(([rr, cc]) => grid[rr][cc] !== EMPTY);
  }

  function popEl(r: number, c: number, cls: string): void {
    const key = `${r},${c}`;
    const node = bubbleEls.get(key);
    bubbleEls.delete(key);
    if (!node) return;
    node.classList.add(cls);
    later(() => node.remove(), 450);
  }

  // ---- Win / lose ----
  function win(): void {
    if (finished) return;
    finished = true;
    sfx.win();
    haptic([20, 40, 20]);
    later(() => opts.onWin({ stars: starsFor(shots, par), moves: shots }), 420);
  }

  function showLose(): void {
    if (finished || overlay) return;
    sfx.lose();
    haptic([10, 30, 10]);
    overlay = el(
      "div.bp-overlay",
      {},
      el(
        "div.bp-overlay-card",
        {},
        el("h3", {}, "Bord vol! 😿"),
        el("p", {}, "De bubbels hebben de lijn bereikt. Probeer het nog eens!"),
        el("button.bp-btn.primary", { onclick: doRestart }, "↺ Opnieuw"),
      ),
    );
    root.append(overlay);
  }

  function hideOverlay(): void {
    overlay?.remove();
    overlay = null;
  }

  // ---- Restart ----
  function doRestart(): void {
    hideOverlay();
    cancelAnimationFrame(raf);
    fieldEl.querySelector(".bp-flying")?.remove();
    grid = cloneGrid(level.grid);
    queueRng = makeRng(queueSeed(level.n));
    current = nextColor(queueRng, grid);
    next = nextColor(queueRng, grid);
    shots = 0;
    finished = false;
    busy = false;
    buildBoard();
    renderShooter();
    updateStatus();
  }

  // ---- Init ----
  buildBoard();
  updateStatus();
  raf = requestAnimationFrame(layout);

  return {
    destroy() {
      cancelAnimationFrame(raf);
      for (const t of timers) clearTimeout(t);
      window.removeEventListener("resize", onResize);
    },
    restart: doRestart,
  };
}

export const bubblePop: GameDef = {
  id: "bubblepop",
  title: "Bubble Pop",
  tagline: "Schiet en knal drie dezelfde kleuren",
  accent: "#2f7fe0",
  icon: "🫧",
  levelCount: Infinity,
  createLevelView,
};
